import React, { useState } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { Download, ChevronDown, ChevronUp, Trash2, Edit, Eye } from 'react-feather';
import { Badge, Button } from 'react-bootstrap';

import { downloadSparepartImportDocument, setDeleteSparepartImportId, setUpdateSparepartImportId } from '../../../actions/sparepartImports';
import ButtonLoader from "../../layout/ButtonLoader";
import ColorPicker from "../../layout/ColorPicker";
import moment from 'moment';
import { usePermission } from '../../../hooks/usePermission';
import { useSelector } from 'react-redux';

const formatNumber = (value, separator = ',') => {
    if (value || value === 0) {
        // If value is already a formatted string, clean it first
        let numValue = value;
        if (typeof value === 'string') {
            // Remove existing commas and convert to number
            numValue = parseFloat(value.replace(/,/g, ''));
        }
        
        // Format with exactly 2 decimal places
        const formattedValue = Number(numValue).toFixed(2);
        return formattedValue.toString().replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, separator);
    }
    return '0.00';
};

const formatQuantity = (value, separator = ',') => {
    if (value || value === 0) {
        // If value is already a formatted string, clean it first
        let numValue = value;
        if (typeof value === 'string') {
            // Remove existing commas and convert to number
            numValue = parseFloat(value.replace(/,/g, ''));
        }
        
        // Format with up to 5 decimal places, removing trailing zeros
        const formattedValue = Number(numValue).toFixed(5).replace(/\.?0+$/, '');
        return formattedValue.toString().replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, separator);
    }
    return '0';
};

const SparepartImportItem = ({
    sparepartImport,
    number,
    downloadSparepartImportDocument,
    setDeleteSparepartImportId,
    setUpdateSparepartImportId
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [showDetails, setShowDetails] = useState(false);
    const [textColor, setTextColor] = useState('#000000');
    const { userCan } = usePermission();
    const { user } = useSelector(state => state.auth);

    const handleDownloadDocument = async () => {
        setIsLoading(true);
        try {
            const result = await downloadSparepartImportDocument(sparepartImport.id, textColor);
            if (result && result.url) {
                // Open download URL in new tab
                window.open(result.url, '_blank');
            }
        } catch (error) {
            console.error('Download failed:', error);
        }
        setIsLoading(false);
    };

    const toggleDetails = () => {
        setShowDetails(!showDetails);
    };

    const formatDate = (dateString) => {
        return moment(dateString).format('DD-MM-YYYY');
    };

    const formatDateTime = (dateString) => {
        return moment(dateString).format('DD-MM-YYYY HH:mm');
    };

    // Check if an item has been updated
    const isItemUpdated = (item) => {
        if (!item.created_at || !item.updated_at) return false;
        return moment(item.created_at).format('YYYY-MM-DD HH:mm:ss') !== moment(item.updated_at).format('YYYY-MM-DD HH:mm:ss');
    };

    // Check if the import has been updated
    const isImportUpdated = (importItem) => {
        if (!importItem.created_at || !importItem.updated_at) return false;
        return moment(importItem.created_at).format('YYYY-MM-DD HH:mm:ss') !== moment(importItem.updated_at).format('YYYY-MM-DD HH:mm:ss');
    };

    // Get the items for display (all imports are now bulk)
    const items = sparepartImport.items || [];
    const itemsCount = items.length;
    const firstThreeItems = items.slice(0, 3);
    
    // Create summary with deleted spare parts highlighted
    const itemsSummaryElements = firstThreeItems.map((item, index) => {
        const isDeleted = item.spare_part?.is_deleted || item.spare_part?.deleted_at;
        const name = item.spare_part?.name || 'Unknown';
        
        return (
            <span key={index}>
                <span className={isDeleted ? 'text-danger fw-bold' : ''}>
                    {name}
                    {isDeleted && <span className="badge bg-danger ms-1 small">O'chirilgan</span>}
                </span>
                {index < firstThreeItems.length - 1 ? ', ' : ''}
            </span>
        );
    });
    
    const hasMoreItems = itemsCount > 3;

    // Determine if the item is deleted
    const isDeleted = !!sparepartImport.deleted_at;

    // Check if the import date is more than 2 days old
    const isImportOld = () => {
        if (!sparepartImport.date) return false;
        
        const importDate = new Date(sparepartImport.date);
        const currentDate = new Date();
        const diffTime = currentDate.getTime() - importDate.getTime();
        const diffDays = diffTime / (1000 * 60 * 60 * 24);
        
        return diffDays > 2;
    };

    // Check if user has superadmin or director role
    const isSuperAdminOrDirector = () => {
        return user?.roles?.some(role => 
            role.name === 'superadmin' || role.name === 'director'
        );
    };

    // Superadmin and director can edit and delete regardless of date, others are restricted by 2-day rule
    const canEdit = userCan('edit sparepart imports') && !isDeleted && 
        (isSuperAdminOrDirector() || !isImportOld());
    const canDelete = userCan('delete sparepart imports') && !isDeleted && 
        (isSuperAdminOrDirector() || !isImportOld());

    // Get import details for display (all imports are now bulk)
    const getImportDetails = () => {
        if (items && items.length > 0) {
            // For bulk imports, we'll show the total quantity
            const totalQuantity = items.reduce((sum, item) => sum + parseFloat(item.quantity || 0), 0);
            return {
                quantity: totalQuantity,
                price: null, // Don't show average price for bulk imports
                total: sparepartImport.total_amount
            };
        }
        
        // Fallback to total amount
        return {
            quantity: null,
            price: null,
            total: sparepartImport.total_amount
        };
    };

    const importDetails = getImportDetails();

    return (
        <>
            <tr className={`${showDetails ? 'expanded-row' : ''} ${isDeleted ? 'table-danger deleted-item' : ''}`}>
                <td>{number}</td>
                <td>
                    <span className="fw-bold text-primary">
                        #{sparepartImport.reference_number || sparepartImport.id}
                    </span>
                    {isImportUpdated(sparepartImport) && (
                        <Badge bg="warning" className="ms-2 small">O'zgartirilgan</Badge>
                    )}
                </td>
                <td>
                    <div>
                        <div className="fw-semibold">{sparepartImport.warehouse?.firm?.name || "---"}</div>
                        <small className="text-muted">{sparepartImport.warehouse?.name || "---"}</small>
                    </div>
                </td>
                <td>
                    <div className="d-flex align-items-center">
                        <Button 
                            variant="outline-secondary"
                            size="sm"
                            className="me-2"
                            onClick={toggleDetails}
                        >
                            {showDetails ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        </Button>
                        <div>
                            <Badge bg="info" className="me-1">{itemsCount} ta element</Badge>
                            {itemsSummaryElements}
                            {hasMoreItems && <span> va yana {itemsCount - 3} ta...</span>}
                        </div>
                    </div>
                </td>
                <td>
                    <div className="fw-semibold">{sparepartImport.supplier?.name || "---"}</div>
                </td>
                <td>
                    <div>
                        {sparepartImport.description ? (
                            <span className="text-wrap">{sparepartImport.description}</span>
                        ) : (
                            <span className="text-muted">-</span>
                        )}
                    </div>
                </td>
                <td>
                    <span className="fw-bold text-success">
                        {formatNumber(importDetails.total)} so'm
                    </span>
                </td>
                <td>
                    <div>{formatDate(sparepartImport.date || sparepartImport.created_at)}</div>
                    <div className="text-muted small">
                        {sparepartImport.importer?.name}
                    </div>
                </td>
                <td>
                    <div className="d-flex gap-2">
                        <ColorPicker 
                            selectedColor={textColor} 
                            onColorChange={setTextColor}
                            disabled={isLoading}
                        />
                        <Button 
                            variant="outline-success"
                            size="sm"
                            onClick={handleDownloadDocument}
                            disabled={isLoading}
                            title="Yuklab olish"
                        >
                            {isLoading ? <ButtonLoader /> : <Download size={14} />}
                        </Button>
                        
                        <Link to={`/sparepart-imports/${sparepartImport.id}`}>
                            <Button
                                variant="outline-info"
                                size="sm"
                                title="Ko'rish"
                            >
                                <Eye size={14} />
                            </Button>
                        </Link>
                        
                        {canEdit && (
                            <Button
                                variant="outline-warning"
                                size="sm"
                                onClick={() => setUpdateSparepartImportId(sparepartImport.id)}
                                title="Tahrirlash"
                            >
                                <Edit size={14} />
                            </Button>
                        )}
                        
                        {canDelete && (
                            <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => setDeleteSparepartImportId(sparepartImport.id)}
                                title="O'chirish"
                            >
                                <Trash2 size={14} />
                            </Button>
                        )}
                        
                        {isDeleted && (
                            <span className="badge bg-danger ms-1">O'chirilgan</span>
                        )}
                        
                        {!isDeleted && !canEdit && !canDelete && !isSuperAdminOrDirector() && (
                            <span className="badge bg-secondary ms-1" title="2 kundan ko'p vaqt o'tganligi sababli o'zgartirib bo'lmaydi">Muddati o'tgan</span>
                        )}
                    </div>
                </td>
            </tr>
            {showDetails && items && items.length > 0 && (
                <tr className={`details-row ${isDeleted ? 'table-danger deleted-item' : ''}`}>
                    <td colSpan="9">
                        <div className="details-container">
                            <h6 className="mb-3">Import elementlari ({items.length})</h6>
                            {isDeleted && (
                                <div className="alert alert-danger mb-3">
                                    <small>Bu import {formatDateTime(sparepartImport.deleted_at)} da o'chirilgan</small>
                                </div>
                            )}
                            <table className="table table-sm table-bordered">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Ehtiyot qism</th>
                                        <th>Miqdori</th>
                                        <th>Narxi</th>
                                        <th>Jami summa</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((item, index) => {
                                        const isSparePartDeleted = item.spare_part?.is_deleted || item.spare_part?.deleted_at;
                                        const itemUpdated = isItemUpdated(item);
                                        return (
                                            <tr key={index}>
                                                <td>{index + 1}</td>
                                                <td>
                                                    <div className={isSparePartDeleted ? 'text-danger fw-bold' : ''}>
                                                        {item.spare_part?.name}
                                                        {isSparePartDeleted && (
                                                            <span className="badge bg-danger ms-2 small">O'chirilgan</span>
                                                        )}
                                                        {itemUpdated && (
                                                            <span className="badge bg-warning ms-2 small">O'zgartirilgan</span>
                                                        )}
                                                    </div>
                                                    <small className="text-muted">{item.spare_part?.unit}</small>
                                                </td>
                                                <td>{formatQuantity(item.quantity)} {item.spare_part?.unit}</td>
                                                <td>{formatNumber(item.price)} so'm</td>
                                                <td>{formatNumber(item.total_amount)} so'm</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td colSpan="4" className="text-end"><strong>Umumiy summa:</strong></td>
                                        <td><strong>{formatNumber(sparepartImport.total_amount)} so'm</strong></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
};

export default connect(null, {
    downloadSparepartImportDocument,
    setDeleteSparepartImportId,
    setUpdateSparepartImportId
})(SparepartImportItem); 